import React from 'react';
import Autosuggest from 'react-autosuggest';
import { IconButton, Label, useTheme } from '@fluentui/react';
import { useGetStorefrontAssetsQuery } from '../../../store/endpoints/StorefrontAssetEndpoints';

// Tag entry for an asset: autosuggest over the tags already in use anywhere in the org, with the
// typed text usable as a brand-new tag on Enter. Suggestions are derived client-side from the asset
// list the library screen already has cached, so there is nothing to debounce and no extra request.

interface IAssetTagInputProps {
    tags: string[];
    onChange: (tags: string[]) => void;
    label?: string;
    disabled?: boolean;
}

const MAX_SUGGESTIONS = 8;

const AssetTagInput: React.FC<IAssetTagInputProps> = (props) => {
    const theme = useTheme();
    const assets = useGetStorefrontAssetsQuery(undefined);
    const [value, setValue] = React.useState('');
    const [suggestions, setSuggestions] = React.useState<string[]>([]);

    const { tags, onChange } = props;

    // Distinct tags across every asset the org can see, sorted so the list reads predictably.
    const knownTags = React.useMemo(() => {
        const seen = new Set<string>();
        (assets.data?.items ?? []).forEach((asset) => {
            (asset.tags ?? []).forEach((tag) => { if (tag) seen.add(tag); });
        });
        return Array.from(seen).sort((a, b) => a.localeCompare(b));
    }, [assets.data]);

    const matching = React.useCallback((typed: string): string[] => {
        const term = typed.trim().toLowerCase();
        const taken = new Set(tags.map((t) => t.toLowerCase()));
        return knownTags
            .filter((tag) => !taken.has(tag.toLowerCase()))
            .filter((tag) => !term || tag.toLowerCase().includes(term))
            .slice(0, MAX_SUGGESTIONS);
    }, [knownTags, tags]);

    const addTag = React.useCallback((tag: string) => {
        const trimmed = tag.trim();
        setValue('');
        setSuggestions([]);
        if (!trimmed) return;
        if (tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return;
        onChange([...tags, trimmed]);
    }, [tags, onChange]);

    const chipStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '2px 2px 2px 8px',
        borderRadius: 8,
        fontSize: 12,
        backgroundColor: theme.palette.themeLighter,
        color: theme.palette.themeDarker,
    };

    return (
        <div>
            <Label>{props.label ?? 'Tags'}</Label>
            <Autosuggest
                suggestions={suggestions}
                multiSection={false}
                shouldRenderSuggestions={() => true}
                onSuggestionsFetchRequested={({ value: typed }) => setSuggestions(matching(typed))}
                onSuggestionsClearRequested={() => setSuggestions([])}
                getSuggestionValue={(suggestion: string) => suggestion}
                renderSuggestion={(suggestion: string) => <div>{suggestion}</div>}
                onSuggestionSelected={(_e, data) => addTag(data.suggestion)}
                inputProps={{
                    placeholder: 'Search tags / add tag (press Enter)',
                    value,
                    disabled: props.disabled,
                    onChange: (_e, { newValue }) => setValue(newValue),
                    style: { width: '100%', boxSizing: 'border-box', height: 'auto' },
                    onKeyDown: (e) => {
                        if (e.key !== 'Enter') return;
                        // Enter always commits the raw text, so a tag nothing else uses can be
                        // created without picking from the list.
                        e.preventDefault();
                        if (value.trim()) addTag(value);
                    },
                }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: '0.5em' }}>
                {tags.map((tag) => (
                    <span key={tag} style={chipStyle}>
                        {tag}
                        <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            title={`Remove ${tag}`}
                            ariaLabel={`Remove ${tag}`}
                            disabled={props.disabled}
                            styles={{ root: { height: 20, width: 20, color: 'inherit' }, icon: { fontSize: 10 } }}
                            onClick={() => onChange(tags.filter((t) => t !== tag))}
                        />
                    </span>
                ))}
            </div>
            <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
                Tags group assets in the picker. Pick an existing tag or type a new one and press Enter.
            </div>
        </div>
    );
};

export default AssetTagInput;
